import type { FC } from 'react'

import { LoaderWrapper, StyledText, Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { TrButton } from '@skytech/ui/Table/TrButton'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { LoadingError } from '@/components/LoadingError'
import RouterLink from '@/components/RouterLink'
import { useSortParams } from '@/hooks/searchParams'
import { useFormattedNumber } from '@/hooks/use-formatted-number'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { BreadCrumbs, Crumb } from '@/modules/admin/drilldown/BreadCrumbs'
import { DrillDownOptions } from '@/modules/admin/drilldown/DrillDownOptions'
import { getArraySumForSameCurrency } from '@/modules/admin/drilldown/helpers'
import { NoResultsWrapper } from '@/modules/admin/drilldown/NoResultsWrapper'
import queries from '@/modules/admin/queries'
import type {
  DrillDownType,
  DrillDownParams,
  CostObjectReport as CostObjectReportType,
} from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { setCostObjectIdAction } from '../context/actions.context'
import { useDrillDownContext } from '../context/index.context'
import { extractCurrencies } from '../utils'

export const CostObjectReport: FC = () => {
  const { locale } = useAppContext()
  const { state } = useDrillDownContext()

  const params: DrillDownParams = {
    type: state.type as DrillDownType,
    typeId: String(state.typeId),
    year: String(state.year),
    period: String(state.period),
  }

  const { data, isError } = useQuery(queries.adminDrillDownCostObject(params), () =>
    adminResources.getDrillDownCostObjectsReport(params)
  )

  const stringMatcher = (item: CostObjectReportType) => [
    item.costObjectName,
    item.countryId,
    item.costObjectCode,
    item.amount,
    item.salaryDeductionAmount,
  ]

  const rowsFiltered =
    data?.rows?.filter(row => {
      const isCurrencyMatch = row.currency === state.currency
      const isAmountNotZero = row.amount !== 0
      const isSalaryDeductionAmountNotZero = row.salaryDeductionAmount !== 0

      const isQueryFilterMet = getQueryFilter(state.search, stringMatcher)(row)

      return (
        isCurrencyMatch && (isAmountNotZero || isSalaryDeductionAmountNotZero) && isQueryFilterMet
      )
    }) || []

  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof CostObjectReportType>(
    'desc',
    'amount'
  )

  const sortedList = getSortedList({
    list: rowsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isError) return <LoadingError />
  if (!data) return <LoaderWrapper isLoading />

  const currencies = extractCurrencies(data.rows)

  const totalAmount: number = getArraySumForSameCurrency(data?.rows, 'amount', state.currency)
  const totalSalaryDeductionAmount: number = getArraySumForSameCurrency(
    data?.rows,
    'salaryDeductionAmount',
    state.currency
  )

  return (
    <div>
      <BreadCrumbs>
        <Crumb
          name={data.entity.name}
          route={APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT}
          params={params}
        />
      </BreadCrumbs>
      <DrillDownOptions
        data={sortedList.map(item => ({
          name: item.costObjectName,
          amount: item.amount,
          'salary deduction': item.salaryDeductionAmount,
          'part of cost': item.amount / totalAmount,
        }))}
        columnFormats={{
          amount: 'amount',
          'salary deduction': 'amount',
          'part of cost': 'percentage',
        }}
        title="Report"
        currencies={currencies}
        hasSearch
      />
      <NoResultsWrapper numOfResults={data.rows.length}>
        <Table>
          <thead>
            <tr>
              <Th id="costObjectName" onClick={handleSort} className="px-4">
                <Translation id="name" />
              </Th>
              <Th id="costObjectCode" onClick={handleSort} className="px-4">
                <Translation id="phone number" />
              </Th>
              <Th id="amount" onClick={handleSort} className="number px-4">
                <Translation id="amount" />
              </Th>
              <Th id="salaryDeductionAmount" onClick={handleSort} className="number px-4">
                <Translation id="salary deduction" />
              </Th>
              <th className="number px-4">
                <Translation id="part of cost" />
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map((product: CostObjectReportType) => (
              <ClickableRow key={product.costObjectId} totalAmount={totalAmount} {...product} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="px-4">
                <StyledText>
                  <Translation id="total" />
                </StyledText>
              </td>
              <td className="px-4" />
              <td className="number px-4">
                <StyledText>
                  <FormattedNumberWithCurrency
                    amount={totalAmount}
                    currency={state.currency}
                    locale={rowsFiltered?.[0]?.locale}
                  />
                </StyledText>
              </td>
              <td className="number px-4">
                <StyledText>
                  <FormattedNumberWithCurrency
                    amount={totalSalaryDeductionAmount}
                    currency={state.currency}
                    locale={rowsFiltered?.[0]?.locale}
                  />
                </StyledText>
              </td>
            </tr>
          </tfoot>
        </Table>
        <ListLoaderComponent />
      </NoResultsWrapper>
    </div>
  )
}

const ClickableRow: FC<CostObjectReportType & { totalAmount: number }> = ({
  costObjectId,
  costObjectName,
  amount,
  salaryDeductionAmount,
  totalAmount = 0,
  currency,
  locale,
  costObjectCode,
  countryId,
}) => {
  const navigate = useNavigate()

  const { state, dispatch } = useDrillDownContext()
  const { formatNumber: formatNumberWithCurrency } = useFormattedNumberWithCurrency()
  const { formatNumber } = useFormattedNumber()

  const onClick = () => {
    const search = new URLSearchParams({
      type: state.type as DrillDownType,
      typeId: String(state.typeId),
      year: String(state.year),
      period: String(state.period),
      costObjectId: String(costObjectId),
      currency: String(state.currency),
    }).toString()

    const pathname = APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT_PRODUCT

    dispatch(setCostObjectIdAction(String(costObjectId)))

    navigate({ pathname, search })
  }

  return (
    <TrButton onClick={onClick}>
      <Td>
        <Highlight pattern={state.search} text={costObjectName} />
      </Td>
      <Td>
        {costObjectName === 'Expired' ? (
          <Highlight pattern={state.search} text={`+${countryId} ${costObjectCode}`} />
        ) : (
          <RouterLink
            to={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId))}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <Highlight pattern={state.search} text={`+${countryId} ${costObjectCode}`} />
          </RouterLink>
        )}
      </Td>
      <Td className="text-right">
        <Highlight
          pattern={state.search}
          text={formatNumberWithCurrency({
            amount,
            currency,
            locale,
          })}
        />
      </Td>
      <Td className="text-right">
        <Highlight
          pattern={state.search}
          text={formatNumberWithCurrency({
            amount: salaryDeductionAmount,
            currency,
            locale,
          })}
        />
      </Td>
      <Td className="text-right">
        {amount > 0 && (
          <>
            <Highlight
              pattern={state.search}
              text={formatNumber({
                amount: (amount / totalAmount) * 100,
                locale,
              })}
            />
            %
          </>
        )}
      </Td>
    </TrButton>
  )
}
